/**
 * themeConfig.js
 *
 * Single source of truth for all CSS theme rules in the mobile web app.
 *
 * There are two theme resolution mechanisms:
 *
 *  1. URL-based themes  (urlThemeRules)
 *     Resolved once at app startup from window.location.href.
 *     Used by App.js to set the initial theme class on #app-container-global.
 *     Typical use-case: white-label deployments identified by hostname.
 *
 *  2. Org-name-based themes  (orgThemeRules)
 *     Resolved at runtime after the FBS API returns the org object.
 *     Used by fbs-common.js → applyOrgTheme() to add CSS classes to
 *     #app-container-global once the org identity is known.
 *     Multiple classes can be applied (all matching rules are applied).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * HOW TO ADD A NEW THEME
 * ─────────────────────────────────────────────────────────────────────────────
 *  URL-based:
 *    Add an entry to `urlThemeRules` with:
 *      condition : (href: string) => boolean
 *      theme     : CSS class name string (space-separated for multiple classes)
 *
 *  Org-name-based:
 *    Add an entry to `orgThemeRules` with:
 *      match     : (orgNameString: string) => boolean
 *                  (orgNameString is `"${org.name} ${org.shortname}".toLowerCase()`)
 *      classes   : string[]  — one or more CSS class names to add
 *      label     : string    — human-readable label used in console.log
 * ─────────────────────────────────────────────────────────────────────────────
 */

/* ── 1. URL-based theme rules ─────────────────────────────────────────────── */
// Checks window.location.href for substrings to apply a theme at app startup.

export const urlThemeRules = [
    {
        condition: (href) =>
            href.includes("adani") || href.includes("azurewebsites"),
        theme: "theme-custom-violet",
    },
    {
        condition: (href) => href.includes("oman"), // @todo: update based on what is really deployed in Oman (hostname, path, etc.)
        theme: "theme-custom-oman",
    },
];

/* ── 2. Org-name-based theme rules ────────────────────────────────────────── */
// Checks the org.name and org.shortname strings for substrings to apply themes at runtime. (async after API response)
// PS: This applies **all** matching rules (not just the first match), so order does not matter.

export const orgThemeRules = [
    {
        match: (org) => org.includes("bial"),
        classes: ["theme-custom-bial"],
        label: "bial",
    },
    {
        match: (org) => org.includes("encalm"),
        classes: ["theme-custom-encalm-light"],
        label: "encalm.light",
    },
    {
        match: (org) => org.includes("mial"),
        classes: ["theme-custom-violet", "theme-custom-mial"],
        label: "mial",
    },
];

/* ── Helpers ──────────────────────────────────────────────────────────────── */

/**
 * Resolves the URL-based theme class string.
 * Returns an empty string if no rule matches.
 * Called once at module load time in App.js.
 */
export const resolveUrlTheme = () => {
    const href = window.location.href;
    for (const rule of urlThemeRules) {
        if (rule.condition(href)) return rule.theme;
    }
    return "";
};

/**
 * Applies org-name-based theme classes to #app-container-global.
 * All matching rules are applied (not just the first match).
 *
 * @param {string|null} orgName       - org.name from API response
 * @param {string|null} orgShortname  - org.shortname from API response
 * @returns {string} orgNameString    - the normalized org string (for further org-specific logic)
 */
export const applyOrgTheme = (orgName, orgShortname) => {
    const orgNameString = (
        (orgName || "") +
        " " +
        (orgShortname || "")
    ).toLowerCase();

    console.log("applyOrgTheme:", orgNameString);

    const appContainer = document.getElementById("app-container-global");
    if (!appContainer) return orgNameString;

    for (const rule of orgThemeRules) {
        if (rule.match(orgNameString)) {
            appContainer.classList.add(...rule.classes);
            console.log("Setting theme:", rule.label);
        }
    }

    return orgNameString;
};
